import React, { useEffect, useMemo, useRef, useState } from 'react';
import { highlightLandingText } from '@/components/landingPrototype/highlightLandingText';

type LandingHeroProps = {
  t: (key: any) => string;
  onGetAccess: () => void;
  onLogin: () => void;
};

export const LandingHero: React.FC<LandingHeroProps> = ({ t, onGetAccess, onLogin }) => {
  const motionEnabled = useMemo(() => {
    try {
      const reduce = window.matchMedia?.('(prefers-reduced-motion: reduce)')?.matches;
      const coarse = window.matchMedia?.('(pointer: coarse)')?.matches;
      return !reduce && !coarse;
    } catch {
      return true;
    }
  }, []);

  const [mousePosition, setMousePosition] = useState({ x: 50, y: 50 });
  const heroRef = useRef<HTMLElement | null>(null);
  const animationFrameRef = useRef<number | null>(null);
  const targetPositionRef = useRef({ x: 50, y: 50 });
  const isAnimatingRef = useRef(false);

  useEffect(() => {
    if (!motionEnabled) return undefined;

    const updatePosition = () => {
      isAnimatingRef.current = true;
      let dxNow = 0;
      let dyNow = 0;
      setMousePosition(prev => {
        const dx = targetPositionRef.current.x - prev.x;
        const dy = targetPositionRef.current.y - prev.y;
        dxNow = Math.abs(dx);
        dyNow = Math.abs(dy);

        const isReturning =
          Math.abs(targetPositionRef.current.x - 50) < 1 &&
          Math.abs(targetPositionRef.current.y - 50) < 1;
        const speed = isReturning ? 0.08 : 0.15;

        if (Math.abs(dx) < 0.01 && Math.abs(dy) < 0.01) return targetPositionRef.current;

        return {
          x: prev.x + dx * speed,
          y: prev.y + dy * speed,
        };
      });

      // Stop loop when close enough to target (reduces idle CPU usage).
      if (dxNow < 0.05 && dyNow < 0.05) {
        isAnimatingRef.current = false;
        animationFrameRef.current = null;
        return;
      }

      animationFrameRef.current = window.requestAnimationFrame(updatePosition);
    };

    const handleMouseMove = (e: MouseEvent) => {
      if (!heroRef.current) return;
      const rect = heroRef.current.getBoundingClientRect();
      const centerX = rect.width / 2;
      const centerY = rect.top + window.innerHeight / 2;

      const deltaX = e.clientX - rect.left - centerX;
      const deltaY = e.clientY - centerY;

      const maxOffset = Math.min(rect.width, window.innerHeight) * 0.5;
      const distance = Math.sqrt(deltaX * deltaX + deltaY * deltaY);

      let limitedDeltaX = deltaX;
      let limitedDeltaY = deltaY;

      if (distance > maxOffset) {
        const ratio = maxOffset / distance;
        limitedDeltaX = deltaX * ratio;
        limitedDeltaY = deltaY * ratio;
      }

      const offsetX = limitedDeltaX * 0.3;
      const offsetY = limitedDeltaY * 0.3;

      targetPositionRef.current = {
        x: 50 + (offsetX / rect.width) * 100,
        y: 50 + (offsetY / window.innerHeight) * 100,
      };

      if (!isAnimatingRef.current) {
        animationFrameRef.current = window.requestAnimationFrame(updatePosition);
      }
    };

    const handleMouseLeave = () => {
      targetPositionRef.current = { x: 50, y: 50 };
      if (!isAnimatingRef.current) animationFrameRef.current = window.requestAnimationFrame(updatePosition);
    };

    const heroElement = heroRef.current;
    if (!heroElement) return undefined;

    heroElement.addEventListener('mousemove', handleMouseMove, { passive: true });
    heroElement.addEventListener('mouseleave', handleMouseLeave);

    return () => {
      if (animationFrameRef.current) {
        window.cancelAnimationFrame(animationFrameRef.current);
      }
      heroElement.removeEventListener('mousemove', handleMouseMove);
      heroElement.removeEventListener('mouseleave', handleMouseLeave);
    };
  }, [motionEnabled]);

  const offsetX = mousePosition.x - 50;
  const offsetY = mousePosition.y - 50;
  const circle1Radius = 10.7;
  const circle2Radius = 35.7;
  const distance = Math.sqrt(offsetX * offsetX + offsetY * offsetY);
  const maxDistance = circle2Radius - circle1Radius - 1;

  let limitedOffsetX = offsetX;
  let limitedOffsetY = offsetY;
  if (distance > maxDistance) {
    const ratio = maxDistance / distance;
    limitedOffsetX = offsetX * ratio;
    limitedOffsetY = offsetY * ratio;
  }

  const getStretch = (index: number) => {
    const stretchDistance = Math.sqrt(
      limitedOffsetX * limitedOffsetX + limitedOffsetY * limitedOffsetY
    );
    return Math.min((stretchDistance / 30) * (1 + index * 0.2), 1.5);
  };

  return (
    <section className="hero" ref={heroRef}>
      <div className="hero-background">
        <div className="concentric-circles">
          <div
            className="circle circle-2"
            style={
              {
                '--mouse-x': '50%',
                '--mouse-y': '50%',
                '--stretch-x': `${1 + (getStretch(1) * Math.abs(limitedOffsetX)) / 100}`,
                '--stretch-y': `${1 + (getStretch(1) * Math.abs(limitedOffsetY)) / 100}`,
                '--origin-x':
                  limitedOffsetX > 0 ? 'left' : limitedOffsetX < 0 ? 'right' : 'center',
                '--origin-y':
                  limitedOffsetY > 0 ? 'top' : limitedOffsetY < 0 ? 'bottom' : 'center',
              } as React.CSSProperties
            }
          />
          <div
            className="circle circle-3"
            style={
              {
                '--mouse-x': '50%',
                '--mouse-y': '50%',
                '--stretch-x': `${1 + (getStretch(2) * Math.abs(limitedOffsetX)) / 100}`,
                '--stretch-y': `${1 + (getStretch(2) * Math.abs(limitedOffsetY)) / 100}`,
                '--origin-x':
                  limitedOffsetX > 0 ? 'left' : limitedOffsetX < 0 ? 'right' : 'center',
                '--origin-y':
                  limitedOffsetY > 0 ? 'top' : limitedOffsetY < 0 ? 'bottom' : 'center',
              } as React.CSSProperties
            }
          />
          <div
            className="circle circle-4"
            style={
              {
                '--mouse-x': '50%',
                '--mouse-y': '50%',
                '--stretch-x': `${1 + (getStretch(3) * Math.abs(limitedOffsetX)) / 100}`,
                '--stretch-y': `${1 + (getStretch(3) * Math.abs(limitedOffsetY)) / 100}`,
                '--origin-x':
                  limitedOffsetX > 0 ? 'left' : limitedOffsetX < 0 ? 'right' : 'center',
                '--origin-y':
                  limitedOffsetY > 0 ? 'top' : limitedOffsetY < 0 ? 'bottom' : 'center',
              } as React.CSSProperties
            }
          />
          <div
            className="circle circle-5"
            style={
              {
                '--mouse-x': '50%',
                '--mouse-y': '50%',
                '--stretch-x': `${1 + (getStretch(4) * Math.abs(limitedOffsetX)) / 100}`,
                '--stretch-y': `${1 + (getStretch(4) * Math.abs(limitedOffsetY)) / 100}`,
                '--origin-x':
                  limitedOffsetX > 0 ? 'left' : limitedOffsetX < 0 ? 'right' : 'center',
                '--origin-y':
                  limitedOffsetY > 0 ? 'top' : limitedOffsetY < 0 ? 'bottom' : 'center',
              } as React.CSSProperties
            }
          />
        </div>
      </div>
      <div className="hero-content">
        <div className="hero-text">
          <h1>{highlightLandingText(t('heroTitle'), ['AI-Platform', 'AI Platform', 'AI'])}</h1>
          <h2>{t('heroSubtitle')}</h2>
          <div className="hero-buttons">
            <button className="cta-button" onClick={onGetAccess} type="button">
              {t('getAccess')}
            </button>
            <button className="login-button" onClick={onLogin} type="button">
              {t('login')}
            </button>
          </div>
        </div>
      </div>
    </section>
  );
};

